import {
  AimOutlined,
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Skeleton } from "antd";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import moment from "moment";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as Edit } from "../../assets/Icons/portfolioEditIcon.svg";
import { ReactComponent as Trash } from "../../assets/Icons/portfolioTrashIcon.svg";

import EllipsisMenu from "../../Reuseable/EllipsisMenu/EllipsisMenu";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../DashboardPage/components/PanelCard";
import "./TargetAchivementsPage.scss";
import DeleteUserModal from "../../components/AgencyComp/UserComp/DeleteUserModal";
import TargetAchievementsModal from "../../components/TargetAchivements/TargetAchievementsModal/TargetAchievementsModal";
import { useNavigate } from "react-router-dom";
import { fetchTargets } from "../../store/targetSlice";
import { fetchUserFilters } from "../../store/filters";
import { updateTarget, deleteTarget } from "../../assets/api/targets";
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import CustomSelect from "../../Reuseable/Select/Select";
import RangePickerWithPresets from "../../Reuseable/RengePicker/RangePickerWithPresets";
import {
  TARGET_TYPE_CHOICES,
  targetTypeLabel,
} from "../../components/TargetAchivements/targetTypes";

const EMPTY = "—";

/* Every column rendered real markup over the blank placeholder rows while the
   request was in flight - "NA", zeros, "No target", and a working Edit/Delete
   menu on rows that stood for nothing. Each cell renders a bar instead. */
const CellSkeleton = () => <Skeleton.Input size="small" active />;
const WideCellSkeleton = () => <Skeleton.Input size="small" active block />;

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

/* A target of zero cannot be "x% achieved", so the ratio is only reported
   when there is something to measure against. */
const progressOf = (achieved, target) => {
  const a = Number(achieved) || 0;
  const t = Number(target) || 0;
  if (t <= 0) return null;
  return Math.min(Math.round((a / t) * 100), 999);
};

const bandOf = (percent) => {
  if (percent === null) return "is-unset";
  if (percent >= 100) return "is-met";
  if (percent >= 60) return "is-close";
  return "is-behind";
};

/* What the endpoint expects for start_date / end_date. */
const toApiDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD") : undefined);

const NO_FILTERS = { dateRange: [], targetType: undefined, portfolio: undefined };

/* CustomSelect's own defaults are antd's #1890ff and a grey selected row.
   These are the tokens antd bakes into the generated CSS, so the focus ring,
   the search caret and the selected option all come out brand. */
const BRAND_SELECT_THEME = {
  colorPrimary: "#ff6843",
  colorPrimaryHover: "#ff6843",
  optionSelectedBg: "#fff2ee",
  optionSelectedColor: "#d9522f",
  optionActiveBg: "#fff2ee",
};

/* Compares two filter sets by what they would actually send, so re-picking
   the same portfolio from the dropdown does not light up Apply. */
const signatureOf = (filters) =>
  [
    toApiDate(filters.dateRange[0]) || "",
    toApiDate(filters.dateRange[1]) || "",
    filters.targetType || "",
    filters.portfolio ?? "",
  ].join("|");

const formatDate = (value) => {
  const parsed = moment(value, ["MMM D, YYYY", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : value || EMPTY;
};

const TargetAchievementsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  /* Two copies on purpose. `draft` is what the controls hold, `applied` is
     what the table is showing - touching a control used to fire a request per
     keystroke of the selection, so a three-field change cost three fetches
     and two throwaway result sets. Only Apply promotes one to the other.
     dateRange is a dayjs pair while a period is picked, [] while it is not;
     the empty array is what tells the controlled picker it has nothing to
     show. */
  const [draftFilters, setDraftFilters] = useState(NO_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(NO_FILTERS);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [savingTarget, setSavingTarget] = useState(false);
  const [deletingTarget, setDeletingTarget] = useState(false);

  const { targets_data, targets_loading } = useSelector((state) => state.targets);
  const { token } = useSelector((state) => state.auth);
  const { filter_list, loading: filtersLoading } =
    useSelector((state) => state.filters) || {};

  /* The portfolio dropdown is fed by the same options endpoint the Add New
     Target page uses, so the two lists cannot drift apart. */
  useEffect(() => {
    dispatch(fetchUserFilters());
  }, [dispatch]);

  const portfolioOptions = useMemo(() => {
    const rows =
      filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return rows.map((row) => ({
      value: row.id ?? row,
      label: row.name ?? row.key ?? row,
    }));
  }, [filter_list]);

  /* Built from `applied`, never from the draft, so an in-progress selection
     cannot reach the request. */
  const filterQuery = useMemo(
    () => ({
      start_date: toApiDate(appliedFilters.dateRange[0]),
      end_date: toApiDate(appliedFilters.dateRange[1]),
      target_type: appliedFilters.targetType,
      portfolio: appliedFilters.portfolio,
    }),
    [appliedFilters]
  );

  /* Every refetch - the effect, a save, a delete - goes through here so the
     active filters are never dropped on the way. */
  const loadTargets = useCallback(
    (page = currentPage, size = pageSize) =>
      dispatch(fetchTargets({ page, page_size: size, ...filterQuery })),
    [dispatch, currentPage, pageSize, filterQuery]
  );

  useEffect(() => {
    loadTargets();
  }, [loadTargets]);

  /* Editing a control only moves the draft - no request goes out here. */
  const patchDraft = (patch) =>
    setDraftFilters((prev) => ({ ...prev, ...patch }));

  const handleDateChange = (start, end) =>
    patchDraft({ dateRange: start && end ? [start, end] : [] });

  const handleTargetTypeChange = (value) =>
    patchDraft({ targetType: value || undefined });

  const handlePortfolioChange = (value) =>
    patchDraft({ portfolio: value || undefined });

  const isDirty = signatureOf(draftFilters) !== signatureOf(appliedFilters);
  const appliedCount = [
    appliedFilters.dateRange.length === 2,
    Boolean(appliedFilters.targetType),
    Boolean(appliedFilters.portfolio),
  ].filter(Boolean).length;

  /* Narrowing the result set while sitting on page 4 would ask for a page the
     filtered list may not have, so applying starts over at the first. */
  const handleApply = () => {
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setDraftFilters(NO_FILTERS);
    setAppliedFilters(NO_FILTERS);
    setCurrentPage(1);
  };

  /* A chip's cross is an explicit "drop this one", so it takes effect at
     once - it is not a pending edit waiting on Apply. Both copies move
     together, which is also what keeps the control in step with the chip. */
  const dropFilter = (patch) => {
    setDraftFilters((prev) => ({ ...prev, ...patch }));
    setAppliedFilters((prev) => ({ ...prev, ...patch }));
    setCurrentPage(1);
  };

  /* What is currently narrowing the table, spelled out. A row of controls
     alone makes you re-read three widgets to answer "what am I looking at";
     each chip names its field, shows the resolved label rather than the id
     that went over the wire, and drops just that one filter. Read off
     `applied`, so a draft the user has not applied yet is not claimed as
     active. Computed per render rather than memoised so the removers cannot
     close over stale state. */
  const activeFilters = [];
  if (appliedFilters.dateRange.length === 2) {
    activeFilters.push({
      key: "duration",
      field: "Duration",
      value: `${dayjs(appliedFilters.dateRange[0]).format(
        "DD MMM YYYY"
      )} – ${dayjs(appliedFilters.dateRange[1]).format("DD MMM YYYY")}`,
      onRemove: () => dropFilter({ dateRange: [] }),
    });
  }
  if (appliedFilters.targetType) {
    activeFilters.push({
      key: "target_type",
      field: "Target type",
      value: targetTypeLabel(appliedFilters.targetType),
      onRemove: () => dropFilter({ targetType: undefined }),
    });
  }
  if (appliedFilters.portfolio) {
    activeFilters.push({
      key: "portfolio",
      field: "Portfolio",
      value:
        portfolioOptions.find(
          (option) => option.value === appliedFilters.portfolio
        )?.label ?? appliedFilters.portfolio,
      onRemove: () => dropFilter({ portfolio: undefined }),
    });
  }

  const handleEditClick = (record) => {
    setSelectedRecord(record);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    setIsDeleteModalOpen(true);
  };

  /* Returns false when the update fails so the dialog stays open with the
     values still in it, rather than closing and losing the edit. */
  const handleSave = async (id, payload) => {
    if (!id) {
      CustomNotification.show("This target has no id to update.", "error");
      return false;
    }

    try {
      setSavingTarget(true);
      const res = await updateTarget(id, payload, token);
      if (res?.status === 200 || res?.status === 201 || res?.status === 204) {
        CustomNotification.show("Target updated successfully", "success");
        loadTargets();
        return true;
      }
      return false;
    } catch (error) {
      CustomNotification.show(
        error?.message || error?.detail || "Failed to update target",
        "error"
      );
      return false;
    } finally {
      setSavingTarget(false);
    }
  };

  const handleDelete = async () => {
    const id = selectedRecord?.id;
    if (!id) {
      CustomNotification.show("This target has no id to delete.", "error");
      return;
    }

    try {
      setDeletingTarget(true);
      const res = await deleteTarget(id, token);
      if (
        res?.status === 200 ||
        res?.status === 202 ||
        res?.status === 204
      ) {
        CustomNotification.show("Target deleted successfully", "success");
        setIsDeleteModalOpen(false);
        setSelectedRecord(null);

        /* Removing the only row on a page would otherwise leave you sitting
           on an empty one; step back instead. Changing the page re-fetches
           through the effect, so only the same-page case refetches here. */
        if (apiData.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          loadTargets();
        }
      }
    } catch (error) {
      CustomNotification.show(
        error?.message || error?.detail || "Failed to delete target",
        "error"
      );
    } finally {
      setDeletingTarget(false);
    }
  };

  const columns = [
    {
      title: "Users",
      dataIndex: "user_name",
      key: "user_name",
      render: (text) =>
        targets_loading ? (
          <CellSkeleton />
        ) : (
        <span className="targetUser">
          <span className="targetUser__avatar" aria-hidden="true">
            {initialsOf(text) || "NA"}
          </span>
          <span className="targetUser__name">{text || EMPTY}</span>
        </span>
        ),
    },
    {
      title: "Target Type",
      dataIndex: "target_type_name",
      key: "target_type_name",
      render: (text) =>
        targets_loading ? <CellSkeleton /> : text || EMPTY,
    },
    {
      title: "Target",
      dataIndex: "target_value",
      key: "target_value",
      render: (value) =>
        targets_loading ? (
          <CellSkeleton />
        ) : (
          <span className="targetNumber">{Number(value) || 0}</span>
        ),
    },
    {
      title: "Achievement",
      dataIndex: "achievements",
      key: "achievements",
      width: 210,
      render: (value, record) => {
        if (targets_loading) return <WideCellSkeleton />;
        const targetVal = Number(record?.target_value) || 0;
        const percent = targetVal > 0 ? Number(record?.progress_percentage) || 0 : null;
        const band = bandOf(percent);

        return (
          <div className={`targetProgress ${band}`}>
            <div className="targetProgress__top">
              <span className="targetProgress__value">
                {Number(value) || 0}
                <i>/ {targetVal}</i>
              </span>
              <span className="targetProgress__pct">
                {percent === null ? "No target" : `${percent}%`}
              </span>
            </div>
            <div className="targetProgress__track">
              <div
                className="targetProgress__fill"
                style={{ width: `${Math.min(percent ?? 0, 100)}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (value) =>
        targets_loading ? (
          <CellSkeleton />
        ) : (
          <span className="targetDate">{formatDate(value)}</span>
        ),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (value) =>
        targets_loading ? (
          <CellSkeleton />
        ) : (
          <span className="targetDate">{formatDate(value)}</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 80,
      render: (_, record) =>
        targets_loading ? (
          <CellSkeleton />
        ) : (
        /* Was a hand-rolled Dropdown + Menu with a bare MoreOutlined; the
           shared menu is what every other table on the app uses. */
        <EllipsisMenu
          options={[
            {
              label: "Edit",
              icon: <Edit />,
              onClick: () => handleEditClick(record),
            },
            {
              label: "Delete",
              icon: <Trash />,
              onClick: () => handleDeleteClick(record),
            },
          ]}
        />
        ),
    },
  ];

  const apiData = targets_data?.results || [];

  /* Array(5).fill({}) handed every placeholder row the same object and no id,
     so they all leaned on the index fallback in rowKey. */
  const skeletonRows = useMemo(
    () => Array(5).fill(0).map((_, i) => ({ id: `skeleton-${i}` })),
    []
  );

  const summary = useMemo(() => {
    const totalCount = targets_data?.count || 0;
    const withTarget = apiData.filter((row) => Number(row.target_value) > 0);
    const met = withTarget.filter(
      (row) => Number(row.achievements) >= Number(row.target_value)
    ).length;

    const average = withTarget.length
      ? Math.round(
        withTarget.reduce(
          (total, row) =>
            total + (Number(row.progress_percentage) || 0),
          0,
        ) / withTarget.length,
      )
      : null;

    return [
      // { label: "Users tracked", value: totalCount },
      // {
      //   label: "Targets set",
      //   value: `${withTarget.length} of ${totalCount > apiData.length ? totalCount : apiData.length}`, // Approximation if totalCount > page_size
      // },
      // // { label: "Targets met", value: withTarget.length ? met : EMPTY },
      // {
      //   label: "Average achievement",
      //   value: average === null ? EMPTY : `${average}%`,
      // },
    ];
  }, [apiData, targets_data?.count]);

  return (
    <div className="targetAchievementsMain">
      <div className="targetSummary">
        {summary.map(({ label, value }) => (
          <div className="targetSummary__item" key={label}>
            <span className="targetSummary__label">{label}</span>
            <span className="targetSummary__value">{value}</span>
          </div>
        ))}
      </div>

      {/* ---- filters ---- */}
      <PanelCard
        title="Filters"
        subtitle={
          isDirty
            ? "Unapplied changes"
            : appliedCount
            ? `${appliedCount} applied`
            : "Showing all targets"
        }
        icon={<FilterOutlined />}
        accent="brand"
        className="targetFilterCard"
        actions={
          <div className="targetFilters__actions">
            <button
              type="button"
              className="targetFilters__clear"
              onClick={handleClearFilters}
              /* nothing selected or applied means nothing to clear - the
                 button says so rather than firing a refetch that changes
                 nothing */
              disabled={!appliedCount && !isDirty}
            >
              <ReloadOutlined />
              Clear all
            </button>
            <button
              type="button"
              className="targetFilters__apply"
              onClick={handleApply}
              /* the draft already matches what the table is showing, so
                 there is nothing to apply */
              disabled={!isDirty}
            >
              <CheckOutlined />
              Apply filters
            </button>
          </div>
        }
      >
        <div className="targetFilters">
          <div className="targetFilters__grid">
            <div className="targetFilters__field">
              <span className="upper-label">Duration</span>
              <RangePickerWithPresets
                value={draftFilters.dateRange}
                onDateChange={handleDateChange}
              />
            </div>

            <div className="targetFilters__field">
              <CustomSelect
                label="Target Type"
                placeholder="All target types"
                value={draftFilters.targetType}
                options={TARGET_TYPE_CHOICES}
                onChange={handleTargetTypeChange}
                allowClear
                /* CustomSelect defaults these to antd blue and a grey
                   selected row; the whole app is brand orange. */
                selectTheme={BRAND_SELECT_THEME}
              />
            </div>

            <div className="targetFilters__field">
              <CustomSelect
                label="Portfolio"
                placeholder="All portfolios"
                value={draftFilters.portfolio}
                options={portfolioOptions}
                loading={filtersLoading}
                onChange={handlePortfolioChange}
                allowClear
                showSearch
                selectTheme={BRAND_SELECT_THEME}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div className="targetFilters__applied">
              <span className="targetFilters__appliedLabel">Applied</span>
              {activeFilters.map((filter) => (
                <span className="targetChip" key={filter.key}>
                  <span className="targetChip__field">{filter.field}</span>
                  <span className="targetChip__value">{filter.value}</span>
                  <button
                    type="button"
                    className="targetChip__remove"
                    onClick={filter.onRemove}
                    aria-label={`Remove ${filter.field} filter`}
                  >
                    <CloseOutlined />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </PanelCard>

      <PanelCard
        title="Targets"
        icon={<AimOutlined />}
        accent="brand"
        className="targetTableCard"
        actions={
          <Button
            className="addTargetBtn"
            icon={<PlusOutlined />}
            onClick={() => navigate("/target-achievement/add-new-target")}
          >
            Add New Target
          </Button>
        }
      >
        <ReusableTable
          columns={columns}
          dataSource={targets_loading ? skeletonRows : apiData}
          rowKey={(record, index) => record?.id ?? index}
          pageSize={pageSize}
          CurrentPageNo={currentPage}
          totalItems={targets_data?.count || apiData.length}
          setPageSize={setPageSize}
          triggerChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
        />
      </PanelCard>

      <TargetAchievementsModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        userData={selectedRecord}
        onSave={handleSave}
        loading={savingTarget}
      />

      {/* Delete Modal */}
      <DeleteUserModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        /* Without these the shared dialog asked "Enable this user?" and
           offered an Enable button for what is a target deletion. */
        message="Delete this target?"
        confirmLabel="Delete"
        danger
        loading={deletingTarget}
        /* closing is left to handleDelete, so a failed delete keeps the
           dialog open instead of dismissing as though it worked */
        onDelete={handleDelete}
      />
    </div>
  );
};

export default TargetAchievementsPage;
